// ── How many reminders one item may carry, and who may use Alarm ────────────
// Notification reminders: free up to 5 per task / event / habit, Premium
// unlimited. Alarm reminders (the full-screen, ringing, snoozable kind) are
// Premium only, up to 5 per item.
//
// Deliberately a UI-side limit only. The data model, the native scheduler and
// the server push all just take whatever array and alertType they're handed,
// and that is on purpose: an item that already has an alarm, or more reminders
// than the free cap (made while Premium was active, or before these limits
// existed), must keep ringing on all of them, or someone's alarm silently
// stops. Free users can only remove from such an item, never add to it.
export const FREE_NOTIFICATION_LIMIT = 5;
export const ALARM_LIMIT = 5;

// How many reminders `alertType` may hold for this plan. Infinity = no cap.
export function reminderLimit(unlocked, alertType = "notification") {
  if (alertType === "alarm") return unlocked ? ALARM_LIMIT : 0;
  return unlocked ? Infinity : FREE_NOTIFICATION_LIMIT;
}

// Free accounts can't pick Alarm, but an item that is already an Alarm keeps it
// (it only locks once they switch that item to Notification).
export const alarmLocked = (unlocked, alertType) => !unlocked && alertType !== "alarm";

// May this item switch to Alarm right now? It must hold no more reminders than
// an alarm allows; otherwise the user trims first rather than losing some
// silently.
export const canSwitchToAlarm = (sel, unlocked) =>
  unlocked && (Array.isArray(sel) ? sel.length : 0) <= ALARM_LIMIT;

// What tapping offset `m` does to the current selection `sel`, as the new
// sorted array — or null when the tap isn't allowed (so the caller can leave
// things alone).
//   - Tapping a selected offset always removes it, on any plan.
//   - Otherwise it adds, until the cap for this plan and alert type.
export function nextReminders(sel, m, unlocked, alertType = "notification") {
  const cur = Array.isArray(sel) ? sel : [];
  const sorted = (arr) => [...new Set(arr)].sort((a, b) => a - b);
  if (cur.includes(m)) return sorted(cur.filter(x => x !== m));
  return cur.length < reminderLimit(unlocked, alertType) ? sorted([...cur, m]) : null;
}

// Why a tap on an unselected offset would do nothing right now, for the hint
// under the chips. null = nothing to say.
export function reminderLimitHint(sel, unlocked, alertType = "notification") {
  const n = Array.isArray(sel) ? sel.length : 0;
  if (alertType === "alarm") {
    if (!unlocked) return "Alarm reminders are Premium. Yours keep ringing, but you can't add more.";
    return n >= ALARM_LIMIT ? `Alarms: up to ${ALARM_LIMIT} reminders per item.` : null;
  }
  if (unlocked) return null;
  if (n > FREE_NOTIFICATION_LIMIT) return `Free plan: up to ${FREE_NOTIFICATION_LIMIT} reminders per item. Yours keep going, but you can't add more. Unlimited reminders are Premium.`;
  if (n === FREE_NOTIFICATION_LIMIT) return `Free plan: up to ${FREE_NOTIFICATION_LIMIT} reminders per item. Premium has unlimited.`;
  return null;
}
